import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { DatabaseBackup, Upload, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BackupRestoreCard() {
  const [dpwd, setDpwd] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [rpwd, setRpwd] = useState('');
  const [restoring, setRestoring] = useState(false);
  const fileRef = useRef(null);

  const downloadBackup = async () => {
    if (!dpwd) return toast.error('Enter your password');
    try {
      setDownloading(true);
      const res = await axios.get(`${API_URL}/api/backup/download?password=${encodeURIComponent(dpwd)}`,
        { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      const filename = `manha_backup_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '')}.mdb`;
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Backup downloaded');
      setDpwd('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Backup failed');
    } finally { setDownloading(false); }
  };

  const uploadRestore = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error('Pick a backup file first');
    if (!rpwd) return toast.error('Enter the backup password');
    try {
      setRestoring(true);
      const text = await file.text();
      // File was base64 stream from backend
      const payload_b64 = text.trim();
      const { data } = await axios.post(`${API_URL}/api/backup/restore`,
        { password: rpwd, payload_b64 }, { withCredentials: true });
      const total = Object.values(data.restored || {}).reduce((a, b) => a + b, 0);
      toast.success(`Restore complete — ${total} docs`);
      setRpwd(''); if (fileRef.current) fileRef.current.value = '';
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Restore failed');
    } finally { setRestoring(false); }
  };

  return (
    <Card className="bg-slate-950/40 border-emerald-900" data-testid="backup-card">
      <CardHeader>
        <div className="flex items-center space-x-2">
          <ShieldCheck className="h-5 w-5 text-emerald-500" />
          <h2 className="text-lg font-semibold text-white">Full Backup &amp; Restore</h2>
        </div>
        <p className="text-xs text-slate-400 mt-1">
          Snapshots include every collection except runtime logs. File is XOR-obfuscated with your password — keep both safe.
        </p>
      </CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-emerald-500 flex items-center"><DatabaseBackup className="h-4 w-4 mr-2" /> Download</h3>
          <Label className="text-slate-300 text-xs">Your password</Label>
          <Input type="password" value={dpwd} onChange={(e) => setDpwd(e.target.value)}
            data-testid="backup-download-password"
            className="bg-slate-800 border-slate-700 text-white" />
          <Button onClick={downloadBackup} disabled={downloading}
            data-testid="backup-download-btn"
            className="w-full bg-emerald-600 hover:bg-emerald-700">
            <DatabaseBackup className="h-4 w-4 mr-2" /> {downloading ? 'Preparing...' : 'Download Full Backup'}
          </Button>
        </div>

        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-amber-500 flex items-center"><Upload className="h-4 w-4 mr-2" /> Restore</h3>
          <Label className="text-slate-300 text-xs">Backup file (.mdb)</Label>
          <Input type="file" ref={fileRef} data-testid="backup-restore-file" accept=".mdb,.txt,.b64"
            className="bg-slate-800 border-slate-700 text-white cursor-pointer" />
          <Label className="text-slate-300 text-xs">Password used at backup</Label>
          <Input type="password" value={rpwd} onChange={(e) => setRpwd(e.target.value)}
            data-testid="backup-restore-password"
            className="bg-slate-800 border-slate-700 text-white" />
          <Button variant="outline" onClick={uploadRestore} disabled={restoring}
            data-testid="backup-restore-btn"
            className="w-full border-amber-500 text-amber-500 hover:bg-amber-500 hover:text-white">
            <Upload className="h-4 w-4 mr-2" /> {restoring ? 'Restoring...' : 'Upload & Restore'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
